import dayjs from 'dayjs';
import { z } from 'zod';

/** An untouched number input gives '' which should read as "missing", not 0. */
const optionalNumber = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z.number().optional(),
);

const requiredNumber = (requiredMessage: string) =>
  optionalNumber
    .refine((value) => value !== undefined, requiredMessage)
    .transform((value) => value as number);

export const inventorySchema = z
  .object({
    productName: z.string().trim().min(1, 'Please provide a product name'),
    category: z.string().min(1, 'Please select a category'),
    sellingPrice: requiredNumber('Please provide a selling price')
      .refine((v) => v >= 0, 'The value cannot be negative')
      .refine((v) => v <= 100, 'Max value is 100'),
    costPrice: requiredNumber('Please provide a cost price').refine(
      (v) => v >= 10,
      'Min value is 10',
    ),
    quantityInStock: requiredNumber('Please provide a quantity in stock')
      .refine((v) => Number.isInteger(v), 'Use a whole number')
      .refine((v) => v >= 0, 'The value cannot be negative'),
    orderType: z.string().min(1, 'Please select an order type'),
    addDiscount: z.boolean(),
    discountType: z.string().optional(),
    discountValue: optionalNumber.refine((v) => v === undefined || v >= 0, 'Min 0 please'),
    addExpiryDate: z.boolean(),
    expiryDate: z.string().optional(),
    expiryTime: z.string().optional(),
    longDescription: z.string().trim().min(1, 'Please provide long description'),
  })
  .superRefine((data, ctx) => {
    if (data.addDiscount && !data.discountType) {
      ctx.addIssue({
        code: 'custom',
        path: ['discountType'],
        message: 'Please select a discount type',
      });
    }
    if (data.addExpiryDate && !(data.expiryDate && dayjs(data.expiryDate).isAfter('2010-01-01'))) {
      ctx.addIssue({
        code: 'custom',
        path: ['expiryDate'],
        message: 'Please select a date after 2010',
      });
    }
  });

export type InventoryFormInput = z.input<typeof inventorySchema>;
export type InventoryFormValues = z.output<typeof inventorySchema>;
